import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../assets/Logo1.png";
import { logout } from "../utils/api";
import { AxiosError } from "./sessionexpiry";
import { makeAuthenticatedRequest } from "../utils/api";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { useDispatch, useSelector } from "react-redux";
import { login_users_realtime_details } from "../redux/LoginSlice";

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState({
    userManagement: false,
    Configuration: false,
  });

  // Map paths to link names
  const getActiveLink = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "dashboard";
    if (path === "/resellers") return "Resellers";
    if (path === "/collections") return "Collections";
    if (path === "/payouts") return "payouts";
    if (path === "/wallet") return "wallet";
    if (path === "/beneficaries") return "Beneficaries";
    if (path === "/commissions") return "Commissions";
    if (path === "/settlement") return "Settlement";
    if (path === "/monthlyinvoice") return "Monthly invoices";
    if (path === "/profile") return "profile";
    if (path === "/accountwhitelist") return "Account Whitelist";
    if (path === "/DeveloperPortal") return "DeveloperPortal";
    if (path === "/users") return "Users";
    return "";
  };

  const activeLink = getActiveLink();

  const toggleMenu = (menuName) => {
    setMenuOpen((prevState) => ({
      ...prevState,
      [menuName]: !prevState[menuName],
    }));
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState(null);

  const users = JSON.parse(sessionStorage.getItem("users"));
  const Screen_no = users?.userdatils?.Screen_no || 0;
  const businessName = users?.userdatils?.business_name || "Merchant";
  const businessType = users?.userdatils?.business_type || "";

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const toggleDropdown = (dropdownmenu) => {
    setOpenDropdown((prev) => (prev === dropdownmenu ? null : dropdownmenu));
  };

  useEffect(() => {
    const postData = async () => {
      try {
        const accessToken = sessionStorage.getItem("token");
        const requestData = JSON.stringify({
          Dname: "merchant",
          Tname: "users u",
          Cname: ["u.id", "name", "screen_no", "email", "business_name", "mobile", "profile_pic", "u.business_type", "bt.business_type", "u.created_at"],
          Jtype: "inner join",
          Jcondition: {
            "merchant.business_type bt": "u.business_type=bt.id",
          },
          Where: [
            {
              column: "accesstoken",
              operator: "=",
              value: accessToken,
            },
          ],
          Limit: "10",
          Offset: "0",
        });

        const encrypted = encrypt(requestData);
        const data = await makeAuthenticatedRequest("POST", "/fetch", { encrypted }, navigate);
        const decryptdata = decrypt(data);
        const fixedString = decryptdata.replace(/\\"/g, '"');
        const jsonArray = JSON.parse(fixedString);
        dispatch(login_users_realtime_details(jsonArray));
        setResponseData(jsonArray);
      } catch (err) {
        AxiosError(navigate, error);
        setError(err.message);
      }
    };

    postData();
  }, [dispatch]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      AxiosError(navigate, error);
      console.error("Logout error:", error.message);
    }
  };

  // Nav item component for clean reuse
  const NavItem = ({ to, icon, label, isActive, badge }) => (
    <Link
      to={to}
      className={`sidebar-nav-item group ${isActive ? "sidebar-nav-active" : ""}`}
    >
      <span className={`sidebar-nav-icon material-icons ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
        {icon}
      </span>
      <span className={`sidebar-nav-label ${isActive ? "text-white" : "text-slate-300 group-hover:text-white"}`}>
        {label}
      </span>
      {badge && (
        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-indigo-500/30 text-indigo-300 rounded-full border border-indigo-500/30">
          {badge}
        </span>
      )}
    </Link>
  );

  return (
    <>
      <style>{`
        /* Sidebar Premium Design System */
        .sidebar-root {
          width: 17rem;
          height: 100vh;
          background: linear-gradient(165deg, #0f172a 0%, #1e1b4b 40%, #0f172a 100%);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          z-index: 1000;
          overflow: hidden;
          border-right: 1px solid rgba(99, 91, 255, 0.15);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.4);
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          transform: ${isOpen ? 'translateX(0)' : 'translateX(-100%)'};
        }

        @media (min-width: 768px) {
          .sidebar-root {
            transform: translateX(0) !important;
            z-index: 100;
          }
        }

        /* Subtle animated mesh background */
        .sidebar-root::before {
          content: '';
          position: absolute;
          top: -40%;
          left: -40%;
          width: 80%;
          height: 80%;
          background: radial-gradient(circle, rgba(99, 91, 255, 0.12) 0%, transparent 70%);
          border-radius: 50%;
          animation: sidebarOrb1 20s ease-in-out infinite;
          pointer-events: none;
        }

        .sidebar-root::after {
          content: '';
          position: absolute;
          bottom: -20%;
          right: -30%;
          width: 70%;
          height: 70%;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.10) 0%, transparent 70%);
          border-radius: 50%;
          animation: sidebarOrb2 25s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes sidebarOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, 30px) scale(1.1); }
        }

        @keyframes sidebarOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, -20px) scale(1.15); }
        }

        /* Logo / Brand area */
        .sidebar-logo-area {
          padding: 1.75rem 1.25rem 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.07);
          position: relative;
          z-index: 2;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .brand-icon-wrap {
          width: 3rem;
          height: 3rem;
          border-radius: 0.875rem;
          background: linear-gradient(135deg, #635bff 0%, #8b5cf6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          box-shadow: 0 6px 20px rgba(99, 91, 255, 0.5), inset 0 1px 0 rgba(255,255,255,0.2);
          animation: brandPulse 3s ease-in-out infinite;
        }

        @keyframes brandPulse {
          0%, 100% { box-shadow: 0 6px 20px rgba(99, 91, 255, 0.5), inset 0 1px 0 rgba(255,255,255,0.2); }
          50% { box-shadow: 0 8px 28px rgba(99, 91, 255, 0.75), inset 0 1px 0 rgba(255,255,255,0.2); }
        }

        .brand-name {
          font-size: 1.35rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #ffffff 0%, #a78bfa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.1;
          margin-bottom: 0.3rem;
        }

        .brand-tagline {
          font-size: 0.62rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(148, 163, 184, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
        }

        .brand-tagline::before,
        .brand-tagline::after {
          content: '';
          width: 18px;
          height: 1px;
          background: rgba(148, 163, 184, 0.25);
          border-radius: 1px;
        }

        /* User profile card */
        .sidebar-user-card {
          margin: 0.75rem 1rem;
          padding: 0.875rem 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          position: relative;
          z-index: 2;
          flex-shrink: 0;
          backdrop-filter: blur(10px);
        }

        .sidebar-user-avatar {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.75rem;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 700;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(99, 91, 255, 0.4);
        }

        .sidebar-user-status {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          border: 2px solid #0f172a;
          position: absolute;
          bottom: 2px;
          right: 2px;
        }

        /* Scrollable nav area */
        .sidebar-nav-scroll {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 0.5rem 0.75rem;
          position: relative;
          z-index: 2;
        }

        .sidebar-nav-scroll::-webkit-scrollbar {
          width: 3px;
        }
        .sidebar-nav-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-nav-scroll::-webkit-scrollbar-thumb {
          background: rgba(99, 91, 255, 0.3);
          border-radius: 2px;
        }

        /* Section label */
        .sidebar-section-label {
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(148, 163, 184, 0.5);
          padding: 0.875rem 0.75rem 0.375rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .sidebar-section-label::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1px;
        }

        /* Nav item */
        .sidebar-nav-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.625rem 0.875rem;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          margin-bottom: 2px;
          text-decoration: none;
          position: relative;
          overflow: hidden;
        }

        .sidebar-nav-item::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0);
          transition: background 0.2s ease;
          border-radius: inherit;
        }

        .sidebar-nav-item:hover::before {
          background: rgba(255, 255, 255, 0.05);
        }

        .sidebar-nav-active {
          background: linear-gradient(135deg, #635bff, #7c3aed) !important;
          box-shadow: 0 4px 16px rgba(99, 91, 255, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
        }

        .sidebar-nav-active::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 60%;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 2px 0 0 2px;
        }

        .sidebar-nav-icon {
          font-size: 1.15rem !important;
          width: 1.15rem;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .sidebar-nav-item:hover .sidebar-nav-icon {
          transform: translateX(1px);
        }

        .sidebar-nav-label {
          font-size: 0.8rem;
          font-weight: 500;
          transition: color 0.2s ease;
          white-space: nowrap;
        }

        /* Dropdown toggle item */
        .sidebar-dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.625rem 0.875rem;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          margin-bottom: 2px;
          position: relative;
        }

        .sidebar-dropdown-toggle:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .sidebar-dropdown-toggle.open {
          background: rgba(99, 91, 255, 0.12);
        }

        .sidebar-dropdown-arrow {
          margin-left: auto;
          font-size: 1rem !important;
          color: rgba(148, 163, 184, 0.6);
          transition: transform 0.3s ease, color 0.2s ease;
        }

        .sidebar-dropdown-toggle.open .sidebar-dropdown-arrow {
          transform: rotate(180deg);
          color: #a78bfa;
        }

        /* Dropdown submenu */
        .sidebar-submenu {
          overflow: hidden;
          transition: max-height 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
          opacity: 0;
          max-height: 0;
        }

        .sidebar-submenu.open {
          opacity: 1;
          max-height: 300px;
        }

        .sidebar-submenu-inner {
          padding-left: 1rem;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
          border-left: 1px solid rgba(99, 91, 255, 0.2);
          margin-left: 1.4rem;
          margin-bottom: 0.25rem;
        }

        .sidebar-sub-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          border-radius: 0.625rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          margin-bottom: 2px;
        }

        .sidebar-sub-item:hover {
          background: rgba(255, 255, 255, 0.06);
        }

        .sidebar-sub-active {
          background: rgba(99, 91, 255, 0.2) !important;
          box-shadow: inset 2px 0 0 #635bff;
        }

        /* Logout area */
        .sidebar-footer {
          padding: 0.75rem;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
          position: relative;
          z-index: 2;
          flex-shrink: 0;
        }

        .sidebar-logout-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.625rem 0.875rem;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          width: 100%;
          border: none;
          background: transparent;
          text-align: left;
        }

        .sidebar-logout-btn:hover {
          background: rgba(239, 68, 68, 0.15);
        }

        .sidebar-logout-btn:hover .logout-icon {
          color: #f87171 !important;
        }

        .sidebar-logout-btn:hover .logout-label {
          color: #f87171 !important;
        }

        /* Active indicator dot */
        .active-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.8);
          margin-left: auto;
          flex-shrink: 0;
        }
      `}</style>

      <div className="sidebar-root">
        {/* Brand Text */}
        <div className="sidebar-logo-area">
          {/* <div className="brand-icon-wrap">
            <span className="material-icons" style={{ fontSize: "1.4rem", color: "white" }}>payments</span>
          </div> */}
          <div className="brand-name">Gaadiaa</div>
          <div className="brand-tagline">Trusted Payments</div>
          
          {/* Mobile Close Button */}
          <button 
            onClick={onClose}
            style={{
              position: "absolute",
              top: "1.25rem",
              right: "1.25rem",
              background: "rgba(255,255,255,0.05)",
              border: "none",
              borderRadius: "0.5rem",
              padding: "0.375rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background 0.2s"
            }}
            className="md:hidden hover:bg-white/10"
            title="Close Sidebar"
          >
            <span className="material-icons" style={{ fontSize: "1.1rem", color: "rgba(148, 163, 184, 0.8)" }}>
              close
            </span>
          </button>
        </div>

        {/* User Profile Card */}
        <div className="sidebar-user-card">
          <div style={{ position: "relative" }}>
            <div className="sidebar-user-avatar">
              {getInitials(businessName)}
            </div>
            <div className="sidebar-user-status" />
          </div>
          <div style={{ overflow: "hidden", flex: 1 }}>
            <p style={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "white",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}>
              {businessName}
            </p>
            <p style={{
              fontSize: "0.68rem",
              color: "rgba(148, 163, 184, 0.7)",
              marginTop: "1px",
            }}>
              Merchant Account
            </p>
          </div>
          <Link
            to="/profile"
            style={{
              padding: "0.375rem",
              borderRadius: "0.5rem",
              background: "rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s",
              flexShrink: 0,
            }}
            title="View Profile"
          >
            <span className="material-icons" style={{ fontSize: "1rem", color: "rgba(148, 163, 184, 0.7)" }}>
              person
            </span>
          </Link>
        </div>





        {/* Nav Items */}
        <div className="sidebar-nav-scroll">

          {/* Home Section */}
          <div className="sidebar-section-label">Home</div>
          <Link
            to="/dashboard"
            className={`sidebar-nav-item ${activeLink === "dashboard" ? "sidebar-nav-active" : ""}`}
          >
            <span className={`material-icons sidebar-nav-icon ${activeLink === "dashboard" ? "text-white" : "text-slate-400"}`}>
              dashboard
            </span>
            <span className={`sidebar-nav-label ${activeLink === "dashboard" ? "text-white" : "text-slate-300"}`}>
              Dashboard
            </span>
            {activeLink === "dashboard" && <div className="active-dot" />}
          </Link>



           {/* Merchant Section */}
          {/* {Screen_no === 1 && (
            <>
              <div className="sidebar-section-label">Collections</div>

              <Link
                to="/settlement"
                className={`sidebar-nav-item ${activeLink === "Settlement" ? "sidebar-nav-active" : ""}`}
              >
                <span className={`material-icons sidebar-nav-icon ${activeLink === "Settlement" ? "text-white" : "text-slate-400"}`}>
                  account_balance_wallet
                </span>
                <span className={`sidebar-nav-label ${activeLink === "Settlement" ? "text-white" : "text-slate-300"}`}>
                  Settlement Funds
                </span>
                {activeLink === "Settlement" && <div className="active-dot" />}
              </Link>

              <Link
                to="/collections"
                className={`sidebar-nav-item ${activeLink === "Collections" ? "sidebar-nav-active" : ""}`}
              >
                <span className={`material-icons sidebar-nav-icon ${activeLink === "Collections" ? "text-white" : "text-slate-400"}`}>
                  payments
                </span>
                <span className={`sidebar-nav-label ${activeLink === "Collections" ? "text-white" : "text-slate-300"}`}>
                  Collection
                </span>
                {activeLink === "Collections" && <div className="active-dot" />}
              </Link>
            </>
          )} */}



          {/* Merchant Section */}
          {Screen_no === 1 && (
            <>
              <div className="sidebar-section-label">Payouts</div>

              <Link
                to="/wallet"
                className={`sidebar-nav-item ${activeLink === "wallet" ? "sidebar-nav-active" : ""}`}
              >
                <span className={`material-icons sidebar-nav-icon ${activeLink === "wallet" ? "text-white" : "text-slate-400"}`}>
                  account_balance_wallet
                </span>
                <span className={`sidebar-nav-label ${activeLink === "wallet" ? "text-white" : "text-slate-300"}`}>
                  Inward Funds
                </span>
                {activeLink === "wallet" && <div className="active-dot" />}
              </Link>

              <Link
                to="/payouts"
                className={`sidebar-nav-item ${activeLink === "payouts" ? "sidebar-nav-active" : ""}`}
              >
                <span className={`material-icons sidebar-nav-icon ${activeLink === "payouts" ? "text-white" : "text-slate-400"}`}>
                  payments
                </span>
                <span className={`sidebar-nav-label ${activeLink === "payouts" ? "text-white" : "text-slate-300"}`}>
                  Payout
                </span>
                {activeLink === "payouts" && <div className="active-dot" />}
              </Link>
            </>
          )}

          {/* Configuration Section */}
          {Screen_no === 1 && (
            <>
              <div className="sidebar-section-label">Configuration</div>

              {/* Configuration Dropdown */}
              <div
                className={`sidebar-dropdown-toggle ${menuOpen.Configuration ? "open" : ""}`}
                onClick={() => toggleMenu("Configuration")}
              >
                <span className={`material-icons sidebar-nav-icon ${menuOpen.Configuration ? "text-violet-400" : "text-slate-400"}`}>
                  manage_accounts
                </span>
                <span className={`sidebar-nav-label ${menuOpen.Configuration ? "text-violet-300" : "text-slate-300"}`}>
                  Configuration
                </span>
                <span className="material-icons sidebar-dropdown-arrow">
                  expand_more
                </span>
              </div>

              <div className={`sidebar-submenu ${menuOpen.Configuration ? "open" : ""}`}>
                <div className="sidebar-submenu-inner">
                  <Link
                    to="/accountwhitelist"
                    className={`sidebar-sub-item ${activeLink === "Account Whitelist" ? "sidebar-sub-active" : ""}`}
                  >
                    <span className={`material-symbols-outlined ${activeLink === "Account Whitelist" ? "text-indigo-400" : "text-slate-500"}`}
                      style={{ fontSize: "1.05rem" }}>
                      add_diamond
                    </span>
                    <span className={`sidebar-nav-label ${activeLink === "Account Whitelist" ? "text-indigo-300" : "text-slate-400"}`}>
                      IP Whitelist
                    </span>
                    {activeLink === "Account Whitelist" && <div className="active-dot" style={{ background: "#818cf8" }} />}
                  </Link>

                  <Link
                    to="/DeveloperPortal"
                    className={`sidebar-sub-item ${activeLink === "DeveloperPortal" ? "sidebar-sub-active" : ""}`}
                  >
                    <span className={`material-icons ${activeLink === "DeveloperPortal" ? "text-indigo-400" : "text-slate-500"}`}
                      style={{ fontSize: "1.05rem" }}>
                      webhook
                    </span>
                    <span className={`sidebar-nav-label ${activeLink === "DeveloperPortal" ? "text-indigo-300" : "text-slate-400"}`}>
                      Developer Options
                    </span>
                    {activeLink === "DeveloperPortal" && <div className="active-dot" style={{ background: "#818cf8" }} />}
                  </Link>
                </div>
              </div>
            </>
          )}

          {/* Account Section */}
          <div className="sidebar-section-label">Account</div>
          <Link
            to="/profile"
            className={`sidebar-nav-item ${activeLink === "profile" ? "sidebar-nav-active" : ""}`}
          >
            <span className={`material-icons sidebar-nav-icon ${activeLink === "profile" ? "text-white" : "text-slate-400"}`}>
              account_circle
            </span>
            <span className={`sidebar-nav-label ${activeLink === "profile" ? "text-white" : "text-slate-300"}`}>
              Profile
            </span>
            {activeLink === "profile" && <div className="active-dot" />}
          </Link>

        </div>

        {/* Footer - Logout */}
        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            <span className="material-icons logout-icon" style={{ fontSize: "1.15rem", color: "rgba(148, 163, 184, 0.6)" }}>
              logout
            </span>
            <span className="logout-label sidebar-nav-label" style={{ color: "rgba(148, 163, 184, 0.8)" }}>
              Logout
            </span>
            <span className="material-icons" style={{ marginLeft: "auto", fontSize: "0.85rem", color: "rgba(148, 163, 184, 0.3)" }}>
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
